import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    RedirectSchema,
    CreateRedirectRequestSchema,
    UpdateRedirectRequestSchema,
} from "../schemas/redirect";

// Query params for listing redirects
export interface AdminRedirectListQueryParams extends QueryParams {
    is_active?: boolean;
    expired?: boolean;
    limit?: number;
    cursor?: string;
}

export function createAdminRedirectsApi(http: HttpClient, baseUrl: string) {
    const adminRedirectsBaseUrl = `${baseUrl}/admin/redirects`;

    return {
        /**
         * List all redirects with pagination
         * GET /api/v2/admin/redirects
         */
        list: createEndpointWithPagination<
            z.infer<typeof RedirectSchema>,
            unknown,
            unknown,
            AdminRedirectListQueryParams
        >(http, "GET", "", {
            base: adminRedirectsBaseUrl,
            itemSchema: RedirectSchema,
        }),

        /**
         * Get a single redirect by ID
         * GET /api/v2/admin/redirects/:id
         */
        get: createEndpoint<
            z.infer<typeof RedirectSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "GET", "/:id", {
            base: adminRedirectsBaseUrl,
            responseSchema: RedirectSchema,
            params: ["id"],
        }),

        /**
         * Create a new redirect
         * POST /api/v2/admin/redirects
         */
        create: createEndpoint<
            z.infer<typeof RedirectSchema>,
            z.input<typeof CreateRedirectRequestSchema>
        >(http, "POST", "", {
            base: adminRedirectsBaseUrl,
            responseSchema: RedirectSchema,
        }),

        /**
         * Update an existing redirect
         * PUT /api/v2/admin/redirects/:id
         */
        update: createEndpoint<
            z.infer<typeof RedirectSchema>,
            z.input<typeof UpdateRedirectRequestSchema>,
            QueryParams,
            { id: number }
        >(http, "PUT", "/:id", {
            base: adminRedirectsBaseUrl,
            responseSchema: RedirectSchema,
            params: ["id"],
        }),

        /**
         * Delete a redirect (soft delete)
         * DELETE /api/v2/admin/redirects/:id
         */
        delete: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "DELETE", "/:id", {
            base: adminRedirectsBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["id"],
        }),
    };
}

